// FILE: src/pages/AdminTicketListPage.jsx
// Support requests (plan WP-8 / WP-6, MTO 10.1–10.2). Tabs filter in place:
// Open, Over 48 hours, Resolved, All, each with its count; the tab, the
// kind of help and the channel are kept in the URL
// (?view=&category=&channel=) so they survive reload and Back. Each request
// shows its reference, how it came in (online, phone, email), how long ago
// it was opened and its 48-hour answer target from the server
// (slaDueAt / dueInHours / breached) as icon + words: "Due in 5 h",
// "Overdue by 3 h". Staff log phone and email requests and see the service
// levels from the header.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { fetchTickets } from "../features/ticketSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { FilterTabs, ResponsiveTable, TicketStatusChip } from "../components/admin/AdminBits";
import { SLA_HOURS, formatDateTime, personName, timeAgo } from "../components/admin/format";
import { CHANNELS, ChannelText, SlaChip, channelLabel, slaInfo } from "../components/admin/supportSla";
import { ticketCategoryLabel } from "../i18n/labels";
import usePermissions from "../hooks/usePermissions";

const VIEWS = [
  { value: "open", label: "Open" },
  { value: "overdue", label: "Over 48 hours" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" }
];
const CATEGORIES = ["TECHNICAL", "SERVICE", "CONTENT"];

const EMPTY_COPY = {
  open: { title: "No open requests", description: "Every support request has been resolved. New ones appear here." },
  overdue: {
    title: `Nothing past the ${SLA_HOURS}-hour answer time`,
    description: `Every open request is still within its ${SLA_HOURS} hours.`
  },
  resolved: { title: "No resolved requests yet", description: "Requests you mark as resolved appear here." },
  all: {
    title: "No support requests yet",
    description: "Requests students send from their Support page, and phone or email requests you log, appear here."
  }
};

const time = (t) => new Date(t?.createdAt || 0).getTime() || 0;

export default function AdminTicketListPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { list, loading, error } = useSelector((state) => state.tickets);
  const [params, setParams] = useSearchParams();
  const permissions = usePermissions();
  const [now, setNow] = useState(() => Date.now());

  const view = VIEWS.some((v) => v.value === params.get("view")) ? params.get("view") : "open";
  const category = CATEGORIES.includes(params.get("category")) ? params.get("category") : "all";
  const channel = CHANNELS.includes(params.get("channel")) ? params.get("channel") : "all";

  const load = useCallback(() => {
    setNow(Date.now());
    dispatch(fetchTickets({}));
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // Ages and due times move on while the page is open.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const tickets = useMemo(() => {
    const all = Array.isArray(list) ? list : [];
    return all.filter(
      (t) => (category === "all" || t.category === category) && (channel === "all" || (t.channel || "IN_APP") === channel)
    );
  }, [list, category, channel]);

  const groups = useMemo(() => {
    const open = tickets.filter((t) => t.status !== "RESOLVED").sort((a, b) => time(a) - time(b));
    return {
      open,
      overdue: open.filter((t) => slaInfo(t, now).overdue),
      resolved: tickets.filter((t) => t.status === "RESOLVED").sort((a, b) => time(b) - time(a)),
      all: tickets.slice().sort((a, b) => time(b) - time(a))
    };
  }, [tickets, now]);

  const shown = groups[view] || [];
  const can = permissions.can;
  const firstLoad = loading && (!Array.isArray(list) || list.length === 0);
  const from = `${location.pathname}${location.search}`;

  return (
    <Box>
      <PageHeader
        title="Support requests"
        description={`Answer every request within ${SLA_HOURS} hours. Open requests are listed oldest first.`}
        actions={
          <>
            {can("support.log") && (
              <Button component={RouterLink} to="/admin/support/log" variant="contained" startIcon={<PhoneInTalkOutlinedIcon />}>
                Log a phone or email request
              </Button>
            )}
            {can("support.report") && (
              <Button component={RouterLink} to="/admin/support/report" variant="outlined" startIcon={<InsightsOutlinedIcon />}>
                Service levels
              </Button>
            )}
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          </>
        }
      />

      <Section id="requests" title="Requests" contentSx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 1, alignItems: { md: "center" } }}>
          <TextField
            select
            id="ticket-category"
            label="Kind of help"
            value={category}
            onChange={(e) => setParam("category", e.target.value, "all")}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value="all">All kinds of help</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {ticketCategoryLabel(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            id="ticket-channel"
            label="How it came in"
            value={channel}
            onChange={(e) => setParam("channel", e.target.value, "all")}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All channels</MenuItem>
            {CHANNELS.map((c) => (
              <MenuItem key={c} value={c}>
                {channelLabel(c)}
              </MenuItem>
            ))}
          </TextField>
          {groups.overdue.length > 0 && (
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, color: "warning.main", fontWeight: 600 }}>
              <WarningAmberOutlinedIcon aria-hidden />
              {groups.overdue.length === 1
                ? `1 open request is past its ${SLA_HOURS}-hour answer time`
                : `${groups.overdue.length} open requests are past their ${SLA_HOURS}-hour answer time`}
            </Typography>
          )}
        </Stack>

        <FilterTabs
          label="Request views"
          idPrefix="requests"
          value={view}
          onChange={(v) => setParam("view", v, "open")}
          options={VIEWS.map((v) => ({
            ...v,
            count: groups[v.value].length,
            icon: v.value === "overdue" && groups.overdue.length > 0 ? <WarningAmberOutlinedIcon aria-hidden /> : undefined
          }))}
        />

        <Box id="requests-panel" role="tabpanel" aria-labelledby={`requests-tab-${view}`}>
          {error && !firstLoad && (!Array.isArray(list) || list.length === 0) ? (
            <ErrorState error={error} title="We couldn't load the support requests" onRetry={load} />
          ) : firstLoad ? (
            <LoadingState variant="table" rows={5} label="Loading support requests…" />
          ) : (
            <>
              {error && <ErrorState compact error={error} onRetry={load} />}
              <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the requests" />}</Box>
              {shown.length === 0 ? (
                <EmptyState icon={ChatBubbleOutlineIcon} compact {...EMPTY_COPY[view]} />
              ) : (
                <ResponsiveTable label={`${VIEWS.find((v) => v.value === view).label} support requests`} minWidth={1000}>
                  <TableHead>
                    <TableRow>
                      <TableCell component="th" scope="col">
                        Student
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Reference
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Kind of help
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Status
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Opened
                      </TableCell>
                      <TableCell component="th" scope="col" align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.map((t) => {
                      const student = t.userId && typeof t.userId === "object" ? t.userId : null;
                      const who = personName(student, "Unknown student");
                      const overdue = slaInfo(t, now).overdue;
                      const replies = Array.isArray(t.messages) ? t.messages.length : 0;
                      const reference =
                        t.reference ||
                        String(t._id || "")
                          .slice(-6)
                          .toUpperCase();
                      return (
                        <TableRow key={t._id} hover>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                            {who}
                            {student?.email && student.email !== who && (
                              <Typography sx={{ color: "text.secondary", fontWeight: 400 }}>{student.email}</Typography>
                            )}
                            {t.description && (
                              <Typography
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 400,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  maxWidth: "48ch"
                                }}
                              >
                                {t.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{reference}</TableCell>
                          <TableCell>
                            <Typography>{ticketCategoryLabel(t.category)}</Typography>
                            <ChannelText channel={t.channel} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" sx={{ gap: 0.75, flexWrap: "wrap" }}>
                              <TicketStatusChip status={t.status} />
                              <SlaChip ticket={t} now={now} />
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography component="span" sx={{ display: "block", fontWeight: overdue ? 650 : 400 }}>
                              Opened {timeAgo(t.createdAt, now)}
                            </Typography>
                            <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                              {formatDateTime(t.createdAt)}
                              {replies ? ` · ${replies} ${replies === 1 ? "message" : "messages"}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              component={RouterLink}
                              to={`/admin/support/${t._id}`}
                              state={{ from }}
                              variant={overdue ? "contained" : "outlined"}
                              aria-label={`View request from ${who}, reference ${reference}, opened ${timeAgo(t.createdAt, now)}`}
                            >
                              View request
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ResponsiveTable>
              )}
            </>
          )}
        </Box>
      </Section>
    </Box>
  );
}
